import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, BarChart3, Trash2 } from "lucide-react";
import { usePropertyComparison } from "@/hooks/use-property-comparison";
import { cn } from "@/lib/utils";

interface PropertyComparisonBarProps {
  onCompare: () => void;
}

export function PropertyComparisonBar({ onCompare }: PropertyComparisonBarProps) {
  const { selectedProperties, removeProperty, clearAll, maxSelections } = usePropertyComparison();

  if (selectedProperties.length === 0) return null;

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border shadow-lg animate-slide-up">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Selected Properties Preview */}
          <div className="flex items-center gap-2 flex-1 overflow-x-auto">
            <Badge variant="secondary" className="shrink-0">
              {selectedProperties.length}/{maxSelections}
            </Badge>
            
            <div className="flex gap-2 overflow-x-auto pb-1">
              {selectedProperties.map((property) => (
                <div
                  key={property.id}
                  className="flex items-center gap-2 bg-muted rounded-lg px-3 py-1.5 shrink-0"
                >
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-foreground truncate max-w-[120px]">
                      {property.address}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatCurrency(property.price)}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 shrink-0"
                    onClick={() => removeProperty(property.id)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAll}
              className="gap-1.5"
            >
              <Trash2 className="h-4 w-4" />
              Clear
            </Button>
            <Button
              onClick={onCompare}
              disabled={selectedProperties.length < 2}
              className="gap-1.5"
            >
              <BarChart3 className="h-4 w-4" />
              Compare ({selectedProperties.length})
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
